/**
 * The queued-card state machine.
 *
 * WHAT THIS IS
 *   A pure reducer over the states a card can occupy between "Convert pressed"
 *   and "the edge started work": `queued` -> `retrying(n, N)` -> `stalled` ->
 *   `retry-all-available`. Any progress or completion message resets it to
 *   `idle`, which means "the queued machine has nothing to say — the normal
 *   progress UI owns this card again".
 *
 * WHY IT EXISTS (evidence, 2026-09-05 Mongo write-stall incident)
 *   The card has no single state field: it derives everything from `status`,
 *   `ws`, `result` and `zipResult`, and **no timer transitions any state**
 *   (CORE-PIPELINE-REFERENCE §10). So nothing on the client ever concludes
 *   "scheduled but never started" — the pod poll simply never gives up on a
 *   non-terminal card (correction 22), and the user watches a spinner forever.
 *   Meanwhile the failure path does the opposite and too much: `removeFile`
 *   runs before the "Connection Issue" dialog and deletes the uploaded bytes on
 *   all three edge domains (correction 16), so the offered Retry cannot work.
 *
 *   This machine is the missing middle: it can say "queued, server busy",
 *   "retrying 2 of 5", "not started", and "you can retry all" — and it can say
 *   nothing else. In particular:
 *
 *   INVARIANT: this reducer never produces a "remove card" or "delete bytes"
 *   outcome. There is no state and no field that can express it, and
 *   `keepUploadedBytes` is `true` in every state the machine can reach. That is
 *   the whole point of plan §4.1 ("invert the error branch: keep the card, set
 *   state"): a transient scheduling failure must never destroy the upload.
 *
 * PLAN SECTION
 *   MONGO-STALL-PLAN-v5.2.md §4.1, §4.3, §4.4 ("Queued — server busy, retrying
 *   (n of N)" ... "give up at 5 min -> amber 'Not started — your file is still
 *   here' + one Retry all"), §4.5; corrections 16, 21, 22.
 *
 * WIRED 2026-09-05 behind `featureFlags.clientQueuedState`: `ScheduleCoordinator`
 * owns one state per card and the file card + batch header render from it.
 * `applyHistory` still drives everything else about the card, and feeds this
 * machine its `progress` / `completed` events.
 */

export type QueuedCardStatus =
    /** Not queued: either never scheduled, or progress has taken over. */
    | 'idle'
    /** Accepted (or accepted-and-busy); waiting for the edge to start. */
    | 'queued'
    /** A retry is scheduled; `attempt` of `maxAttempts`. */
    | 'retrying'
    /** The server's expected-start deadline passed with no progress. */
    | 'stalled'
    /** The 5-minute budget is spent; offer one "Retry all". */
    | 'retry-all-available';

/** Every status the reducer can ever emit. Nothing here removes anything. */
export const QUEUED_CARD_STATUSES: readonly QueuedCardStatus[] = [
    'idle', 'queued', 'retrying', 'stalled', 'retry-all-available',
];

export interface QueuedCardState {
    status: QueuedCardStatus;
    /** Which retry attempt is in flight (0 while merely queued). */
    attempt: number;
    /** How many attempts the budget is expected to allow. */
    maxAttempts: number;
    /** Server-supplied Retry-After for the pending wait, when there was one. */
    retryAfterMs: number | null;
    /** Server-supplied instant by which work should have started. */
    expectedStartAtMs: number | null;
    /** Last progress/completion instant seen for this card. */
    lastProgressAtMs: number | null;
    /** Machine-readable why, for the copy layer and for logs. */
    reason: string | null;
    /**
     * Structural guarantee, asserted by the spec on every transition: the
     * uploaded bytes are kept in every reachable state. The scheduling path
     * must never reach `removeFile`.
     */
    readonly keepUploadedBytes: true;
}

export type QueuedCardEvent =
    /** The schedule call was accepted; the server may name an expected start. */
    | { type: 'scheduled'; expectedStartAtMs?: number | null }
    /** 503 (or a 409 in-flight): the server is busy and asked us to wait. */
    | { type: 'server-busy'; status?: number; retryAfterMs?: number | null }
    /** A retry has been scheduled by the retry policy. */
    | { type: 'retry-scheduled'; attempt: number; maxAttempts?: number; delayMs?: number }
    /** The expected-start deadline passed. */
    | { type: 'expected-start-passed'; atMs: number }
    /** The 5-minute retry budget is spent. */
    | { type: 'budget-exhausted' }
    /** A progress message arrived (websocket or pod poll). */
    | { type: 'progress'; atMs: number }
    /** A terminal completion message arrived. */
    | { type: 'completed'; atMs?: number }
    /** The user pressed "Retry all". */
    | { type: 'user-retry-all' };

/** Plan §4.5: 5 rungs in the ladder, so 5 attempts is the expected ceiling. */
export const DEFAULT_MAX_SCHEDULE_ATTEMPTS = 5;

export const INITIAL_QUEUED_CARD_STATE: QueuedCardState = Object.freeze({
    status: 'idle',
    attempt: 0,
    maxAttempts: DEFAULT_MAX_SCHEDULE_ATTEMPTS,
    retryAfterMs: null,
    expectedStartAtMs: null,
    lastProgressAtMs: null,
    reason: null,
    keepUploadedBytes: true,
}) as QueuedCardState;

function next(state: QueuedCardState, patch: Partial<QueuedCardState>): QueuedCardState {
    return { ...state, ...patch, keepUploadedBytes: true };
}

/**
 * True when progress has been seen at or after the expected-start deadline —
 * in which case the deadline passing is not evidence of a stall.
 */
function progressedSinceDeadline(state: QueuedCardState): boolean {
    if (state.lastProgressAtMs === null) return false;
    if (state.expectedStartAtMs === null) return true;
    return state.lastProgressAtMs >= state.expectedStartAtMs;
}

export function queuedCardReducer(
    state: QueuedCardState = INITIAL_QUEUED_CARD_STATE,
    event: QueuedCardEvent,
): QueuedCardState {
    switch (event?.type) {
        case 'scheduled':
            return next(state, {
                status: 'queued',
                attempt: 0,
                retryAfterMs: null,
                expectedStartAtMs: event.expectedStartAtMs ?? null,
                reason: 'scheduled',
            });

        case 'server-busy':
            // Still queued — the server took the request and asked us to wait.
            // The card must not move to an error-looking state for this.
            return next(state, {
                status: 'queued',
                retryAfterMs: event.retryAfterMs ?? null,
                reason: event.status === 409 ? 'schedule-in-flight' : 'server-busy',
            });

        case 'retry-scheduled':
            return next(state, {
                status: 'retrying',
                attempt: event.attempt,
                maxAttempts: event.maxAttempts ?? state.maxAttempts,
                retryAfterMs: event.delayMs ?? state.retryAfterMs,
                reason: 'retry-scheduled',
            });

        case 'expected-start-passed':
            // Only a stall if nothing has actually started in the meantime.
            if (progressedSinceDeadline(state)) return state;
            // Once the budget is spent, "Retry all" is the stronger state and
            // the deadline passing again must not walk it back.
            if (state.status === 'retry-all-available') return state;
            return next(state, {
                status: 'stalled',
                reason: 'expected-start-deadline-passed',
            });

        case 'budget-exhausted':
            return next(state, {
                status: 'retry-all-available',
                retryAfterMs: null,
                reason: 'retry-budget-exhausted',
            });

        case 'progress':
            // A progress message resets the queued machine: the job started.
            return next(state, {
                status: 'idle',
                attempt: 0,
                retryAfterMs: null,
                lastProgressAtMs: event.atMs,
                reason: 'progress',
            });

        case 'completed':
            return next(state, {
                status: 'idle',
                attempt: 0,
                retryAfterMs: null,
                lastProgressAtMs: event.atMs ?? state.lastProgressAtMs,
                reason: 'completed',
            });

        case 'user-retry-all':
            return next(state, {
                status: 'queued',
                attempt: 0,
                retryAfterMs: null,
                reason: 'user-retry-all',
            });

        default:
            return state;
    }
}
